// Auto-install GitNexus CLI qua `npm install -g gitnexus`.
// Stream stdio để user thấy npm progress. Sau khi cài xong → re-detect verify PATH.
// Throws InstallGitnexusError với hint cụ thể cho EACCES / ENOSPC / timeout / generic.
//
// Pattern identical với install-claude-code-via-npm.ts.
import { spawnSync } from "node:child_process";
import {
  detectGitnexusInstallation,
  invalidateGitnexusInstallationCache,
} from "./detect-gitnexus-installation.js";
import { isMachineReadableMode, log } from "./terminal-logger.js";

// 5 phút — npm global install có thể chậm trên mạng yếu.
const NPM_INSTALL_TIMEOUT_MS = 5 * 60 * 1000;

// Package name chính thức của GitNexus trên npm.
const GITNEXUS_PACKAGE = "gitnexus";

export type InstallGitnexusReason =
  | "permission-denied" // EACCES → cần sudo hoặc fix npm prefix
  | "disk-full" // ENOSPC
  | "timeout" // > 5 phút
  | "binary-not-in-path" // install OK nhưng PATH chưa refresh
  | "generic"; // exit non-zero khác

export class InstallGitnexusError extends Error {
  reason: InstallGitnexusReason;
  exitCode: number | null;
  constructor(reason: InstallGitnexusReason, message: string, exitCode: number | null = null) {
    super(message);
    this.name = "InstallGitnexusError";
    this.reason = reason;
    this.exitCode = exitCode;
  }
}

// Phân loại exit từ npm install thành reason cụ thể để caller branch hint.
function classifyNpmFailure(exitCode: number | null, stderrSample: string): InstallGitnexusError {
  const stderr = stderrSample.toLowerCase();
  if (stderr.includes("eacces") || stderr.includes("permission denied")) {
    return new InstallGitnexusError(
      "permission-denied",
      `npm install -g cần quyền. Thử: sudo npm install -g ${GITNEXUS_PACKAGE} hoặc fix npm prefix (npm config set prefix ~/.npm-global).`,
      exitCode,
    );
  }
  if (stderr.includes("enospc") || stderr.includes("no space")) {
    return new InstallGitnexusError("disk-full", "Đĩa đầy. Free disk space rồi thử lại.", exitCode);
  }
  return new InstallGitnexusError(
    "generic",
    `npm install thất bại (exit ${exitCode ?? "null"}). Xem log npm phía trên.`,
    exitCode,
  );
}

// Main API: cài GitNexus global + verify. Throws InstallGitnexusError nếu fail.
// Return version + path detect được (luôn non-null nếu success).
export function installGitnexusViaNpm(): { version: string | null; path: string } {
  log.info("Đang cài GitNexus qua npm (có thể mất 1-2 phút)...");

  const result = spawnSync("npm", ["install", "-g", GITNEXUS_PACKAGE], {
    // #280: npm ghi progress THẲNG vào stdout của tiến trình cha (không qua
    // terminal-logger) ⇒ ở chế độ `--json` nó lẫn vào payload y như log người-đọc.
    // fd 2 = stderr của cha: người chạy tay vẫn thấy tiến trình, stdout sạch cho JSON.
    stdio: ["inherit", isMachineReadableMode() ? 2 : "inherit", "pipe"],
    timeout: NPM_INSTALL_TIMEOUT_MS,
    encoding: "utf8",
  });

  if (result.signal === "SIGTERM") {
    throw new InstallGitnexusError(
      "timeout",
      `npm install timeout sau ${NPM_INSTALL_TIMEOUT_MS / 1000}s. Check mạng rồi thử lại.`,
      null,
    );
  }

  if (result.status !== 0) {
    if (result.stderr) process.stderr.write(result.stderr);
    throw classifyNpmFailure(result.status, result.stderr || "");
  }

  // v1.14.1 BUG FIX: Invalidate memoize cache TRƯỚC re-detect.
  // Trước fix: cache giữ `{installed: false}` từ probe pre-install →
  // re-detect sau install trả false → throw "binary-not-in-path" SAI dù
  // binary đã thực sự cài vào /opt/homebrew/bin/gitnexus.
  invalidateGitnexusInstallationCache();

  // Re-detect — npm install OK không đồng nghĩa binary đã trong PATH (PATH cache).
  const probe = detectGitnexusInstallation();
  if (!probe.installed || !probe.path) {
    throw new InstallGitnexusError(
      "binary-not-in-path",
      "npm cài xong nhưng `gitnexus` không trong PATH. Reload shell (source ~/.zshrc) hoặc thêm npm global bin vào PATH.",
      null,
    );
  }

  log.success(`Đã cài GitNexus${probe.version ? ` v${probe.version}` : ""} tại ${probe.path}`);
  return { version: probe.version, path: probe.path };
}
